import { AxiosError } from "axios";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { BrandingTarget, Property } from "types/landlord/address";
import { getErrorMessage } from "utils/error";

export type PropertyDictionary = ReturnType<
  typeof useUnitsPropertiesDictionary
>;

export const MAX_BRANDING_TARGETS = 200;

export const BRANDING_TARGETS_PER_PAGE =
  Number(window.localStorage.getItem("per_page")) || 10;

const LANDLORD_FIELDS = [
  "landlord_name",
  "landlord_phone",
  "landlord_email"
] as const;

export type LandlordField = (typeof LANDLORD_FIELDS)[number];

const LANDLORD_FIELD_LABELS: Record<string, string> = {
  landlord_name: "Landlord name",
  landlord_phone: "Landlord phone",
  landlord_email: "Landlord email"
};

export const formatLandlordFields = (fields: string[]) =>
  fields.map((field) => LANDLORD_FIELD_LABELS[field] || field).join(", ");

export const pmsSyncedFields = (target: BrandingTarget) =>
  LANDLORD_FIELDS.filter((field) => target.pms_synced_fields[field]);

export const sourceLandlordFields = (branding: Property["branding"]) =>
  LANDLORD_FIELDS.filter((field) => Boolean(branding?.[field]?.trim()));

export const overriddenPmsFields = (
  target: BrandingTarget,
  sourceFields: LandlordField[]
) => sourceFields.filter((field) => target.pms_synced_fields[field]);

export const getTargetName = (
  target: Pick<BrandingTarget, "name">,
  dictionary: PropertyDictionary
) => target.name || `Unnamed ${dictionary.property}`;

export const propertiesLabel = (
  count: number,
  dictionary: PropertyDictionary
) => (count === 1 ? dictionary.property : dictionary.properties);

export const getApplyErrorMessage = (
  error: unknown,
  dictionary: PropertyDictionary
) => {
  if (
    error instanceof AxiosError &&
    error.response?.data?.error?.code === "batch_size_exceeded"
  )
    return `You can apply branding to at most ${MAX_BRANDING_TARGETS} ${dictionary.properties} at a time.`;

  return getErrorMessage(error);
};
